// src/pages/monitoring/submissions/UploadEvidenceModal.jsx
/**
 * UploadEvidenceModal — Multi-step form upload evidence dari web.
 *
 * Step 1: Pilih FO Segment atau Tower Site
 * Step 2: Pilih Milestone (leaf only)
 * Step 3: Pilih definition + upload file + caption
 *
 * Backend: POST /api/milestones/{id}/evidence/upload (multipart)
 * v2: Evidence langsung ke milestone, tidak butuh progressId.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, Upload, Camera, FileText, Check, Info, ChevronRight,
  Search, AlertTriangle, Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';

import {
  getAvailableSegments,
  getAvailableSites,
  getLeafMilestonesBySegment,
  getLeafMilestonesBySite,
  getEvidenceDefinitions,
  uploadEvidence,
  getMilestoneStatusLabel,
  getMilestoneStatusBg,
  formatFileSize,
  validateFile,
} from '../../../services/fieldSubmissionService';

// ============================================================================
// STEP INDICATOR (reuse same pattern)
// ============================================================================

const StepIndicator = ({ current, labels }) => (
  <div className="flex items-center px-6 pt-4 pb-2">
    {labels.map((label, i) => {
      const step   = i + 1;
      const done   = step < current;
      const active = step === current;
      return (
        <React.Fragment key={step}>
          <div className="flex flex-col items-center">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center
              text-xs font-medium border-2 transition-all ${
              done   ? 'bg-purple-700 border-purple-700 text-white'
            : active ? 'bg-purple-50 border-purple-700 text-purple-700'
            :          'bg-gray-100 border-gray-300 text-gray-400'}`}>
              {done ? <Check className="w-3.5 h-3.5" /> : step}
            </div>
            <span className={`text-xs mt-1 whitespace-nowrap ${
              active ? 'text-purple-700 font-medium' : 'text-gray-400'}`}>
              {label}
            </span>
          </div>
          {i < labels.length - 1 && (
            <div className={`flex-1 h-0.5 mx-2 mb-4 rounded ${
              done ? 'bg-purple-700' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ============================================================================
// STEP 1 — Pilih Scope (sama dengan SubmitProgressModal)
// ============================================================================

const Step1Scope = ({ scopeType, setScopeType, selectedScope, setSelectedScope }) => {
  const [segments, setSegments] = useState([]);
  const [sites,    setSites]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [search,   setSearch]   = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [segs, sts] = await Promise.all([getAvailableSegments(), getAvailableSites()]);
        setSegments(segs);
        setSites(sts);
      } catch {
        toast.error('Gagal memuat data segment/site');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const list     = scopeType === 'FO' ? segments : sites;
  const filtered = list.filter(s =>
    !search
      || (s.name ?? '').toLowerCase().includes(search.toLowerCase())
      || (s.code ?? '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-5 space-y-4">
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-2">Tipe scope</label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { key: 'FO',    label: 'FO Segment', sub: 'Fiber Optic' },
            { key: 'TOWER', label: 'Tower Site',  sub: 'Tower / BTS' },
          ].map(opt => (
            <button
              key={opt.key}
              onClick={() => { setScopeType(opt.key); setSelectedScope(null); }}
              className={`p-3 rounded-xl border-2 text-left transition-all ${
                scopeType === opt.key
                  ? 'border-purple-600 bg-purple-50'
                  : 'border-gray-200 bg-gray-50 hover:border-gray-300'}`}>
              <div className={`text-sm font-medium ${
                scopeType === opt.key ? 'text-purple-700' : 'text-gray-700'}`}>
                {opt.label}
              </div>
              <div className={`text-xs ${
                scopeType === opt.key ? 'text-purple-500' : 'text-gray-400'}`}>
                {opt.sub}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={scopeType === 'FO' ? 'Cari segment...' : 'Cari site...'}
          className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg
            text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 bg-gray-50"
        />
      </div>

      <div className="space-y-2 max-h-52 overflow-y-auto">
        {loading && <p className="text-sm text-gray-400 text-center py-6">Memuat data...</p>}
        {!loading && filtered.map(scope => (
          <button
            key={scope.id}
            onClick={() => setSelectedScope(scope)}
            className={`w-full text-left p-3 rounded-xl border transition-all ${
              selectedScope?.id === scope.id
                ? 'border-purple-500 bg-purple-50'
                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <span className={`text-xs px-1.5 py-0.5 rounded font-mono font-semibold ${
                  scopeType === 'FO'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-purple-100 text-purple-700'}`}>
                  {scope.code}
                </span>
                <p className="text-sm font-medium text-gray-800 mt-1 truncate">{scope.name}</p>
                <p className="text-xs text-gray-400 mt-0.5 truncate">{scope.projectName}</p>
              </div>
              {selectedScope?.id === scope.id && (
                <div className="w-5 h-5 rounded-full bg-purple-600 flex items-center
                  justify-center flex-shrink-0 mt-1">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
            </div>
          </button>
        ))}
        {!loading && filtered.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-6">
            {search ? 'Tidak ada hasil' : 'Tidak ada scope tersedia'}
          </p>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// STEP 2 — Pilih Milestone
// ============================================================================

const Step2Milestone = ({ selectedScope, selectedMilestone, setSelectedMilestone }) => {
  const [milestones, setMilestones] = useState([]);
  const [loading,    setLoading]    = useState(false);

  useEffect(() => {
    if (!selectedScope) return;
    const load = async () => {
      setLoading(true);
      try {
        const fn   = selectedScope.scopeType === 'FO'
          ? getLeafMilestonesBySegment
          : getLeafMilestonesBySite;
        const data = await fn(selectedScope.id);
        setMilestones(data);
      } catch {
        toast.error('Gagal memuat milestone');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedScope]);

  return (
    <div className="p-5">
      <div className="bg-purple-50 border border-purple-100 rounded-xl p-3 mb-4
        flex items-start gap-2">
        <Info className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-medium text-purple-800">{selectedScope?.name}</p>
          <p className="text-xs text-purple-600">{selectedScope?.projectName}</p>
        </div>
      </div>

      <label className="block text-xs font-medium text-gray-500 mb-2">
        Pilih milestone <span className="text-red-500">*</span>
      </label>

      <div className="space-y-2 max-h-60 overflow-y-auto">
        {loading && <p className="text-sm text-gray-400 text-center py-8">Memuat milestone...</p>}
        {!loading && milestones.map(ms => (
          <button
            key={ms.id}
            onClick={() => setSelectedMilestone(ms)}
            className={`w-full text-left p-3 rounded-xl border transition-all ${
              selectedMilestone?.id === ms.id
                ? 'border-purple-500 bg-purple-50'
                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-gray-400 font-mono">{ms.itemCode}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full border font-medium
                    ${getMilestoneStatusBg(ms.status)}`}>
                    {getMilestoneStatusLabel(ms.status)}
                  </span>
                </div>
                <p className="text-sm font-medium text-gray-800 truncate">{ms.name}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-purple-500 rounded-full"
                      style={{ width: `${ms.finalProgress ?? 0}%` }} />
                  </div>
                  <span className="text-xs text-gray-500 flex-shrink-0">
                    {ms.finalProgress ?? 0}%
                  </span>
                </div>
              </div>
              {selectedMilestone?.id === ms.id && (
                <div className="w-5 h-5 rounded-full bg-purple-600 flex items-center
                  justify-center flex-shrink-0">
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
            </div>
          </button>
        ))}
        {!loading && milestones.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-8">
            Tidak ada milestone untuk scope ini
          </p>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// STEP 3 — Upload Form
// ============================================================================

const Step3Upload = ({
  selectedScope, selectedMilestone,
  selectedDefinition, setSelectedDefinition,
  files, setFiles,
  caption, setCaption,
}) => {
  const [definitions, setDefinitions] = useState([]);
  const [loadingDefs, setLoadingDefs] = useState(false);
  const [dragOver,    setDragOver]    = useState(false);
  const inputRef                      = useRef(null);

  useEffect(() => {
    if (!selectedMilestone) return;
    const load = async () => {
      setLoadingDefs(true);
      try {
        const data = await getEvidenceDefinitions(selectedMilestone.id);
        setDefinitions(data);
        if (data.length === 1) setSelectedDefinition(data[0]);
      } catch {
        toast.error('Gagal memuat definisi evidence');
      } finally {
        setLoadingDefs(false);
      }
    };
    load();
  }, [selectedMilestone]);

  const handleFiles = useCallback((incoming) => {
    const newFiles = Array.from(incoming).map(f => {
      const error = validateFile(f, selectedDefinition);
      return { file: f, error, preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : null };
    });
    setFiles(prev => [...prev, ...newFiles].slice(0, 10));
  }, [selectedDefinition, setFiles]);

  const removeFile = (idx) => {
    setFiles(prev => {
      const next = [...prev];
      if (next[idx]?.preview) URL.revokeObjectURL(next[idx].preview);
      next.splice(idx, 1);
      return next;
    });
  };

  const isPhoto = selectedDefinition?.evidenceType === 'PHOTO'
    || !selectedDefinition;

  return (
    <div className="p-5 space-y-4">
      {/* Context */}
      <div className="bg-purple-50 border border-purple-100 rounded-xl p-3">
        <div className="flex gap-6">
          <div>
            <p className="text-xs text-purple-500">Milestone</p>
            <p className="text-sm font-medium text-purple-900 truncate max-w-48">
              {selectedMilestone?.itemCode} — {selectedMilestone?.name}
            </p>
          </div>
        </div>
        <p className="text-xs text-purple-500 mt-1">
          Evidence diupload langsung ke milestone — tidak terikat ke submission progress manapun
        </p>
      </div>

      {/* Definition selector */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-2">
          Tipe evidence <span className="text-red-500">*</span>
        </label>
        {loadingDefs && <p className="text-xs text-gray-400">Memuat definisi...</p>}
        {!loadingDefs && definitions.length > 0 && (
          <div className="space-y-2">
            {definitions.map(def => (
              <button
                key={def.id}
                onClick={() => setSelectedDefinition(def)}
                className={`w-full text-left p-3 rounded-xl border transition-all ${
                  selectedDefinition?.id === def.id
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-start gap-2 min-w-0">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center
                      flex-shrink-0 mt-0.5 ${
                      def.evidenceType === 'PHOTO'
                        ? 'bg-blue-100' : 'bg-purple-100'}`}>
                      {def.evidenceType === 'PHOTO'
                        ? <Camera className="w-3.5 h-3.5 text-blue-600" />
                        : <FileText className="w-3.5 h-3.5 text-purple-600" />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${
                          def.isMandatory
                            ? 'bg-red-100 text-red-700'
                            : 'bg-gray-100 text-gray-500'}`}>
                          {def.isMandatory ? 'Wajib' : 'Opsional'}
                        </span>
                        <span className="text-xs text-gray-400">
                          {def.requirementText}
                        </span>
                        {def.uploadedCount > 0 && (
                          <span className="text-xs text-emerald-600 font-medium">
                            ✓ {def.uploadedCount} sudah diupload
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-medium text-gray-800 truncate">{def.name}</p>
                      {def.description && (
                        <p className="text-xs text-gray-400 truncate">{def.description}</p>
                      )}
                    </div>
                  </div>
                  {selectedDefinition?.id === def.id && (
                    <div className="w-5 h-5 rounded-full bg-purple-600 flex items-center
                      justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
        {!loadingDefs && definitions.length === 0 && (
          <div className="text-xs text-gray-400 bg-gray-50 border border-gray-200
            rounded-xl p-3 flex items-center gap-2">
            <Info className="w-3.5 h-3.5 flex-shrink-0" />
            Tidak ada definisi evidence untuk milestone ini. Upload tetap bisa dilakukan tanpa definisi.
          </div>
        )}
      </div>

      {/* Upload zone */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">
          File <span className="text-red-500">*</span>
        </label>
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => {
            e.preventDefault();
            setDragOver(false);
            handleFiles(e.dataTransfer.files);
          }}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer
            transition-all ${dragOver
              ? 'border-purple-500 bg-purple-50'
              : 'border-gray-300 bg-gray-50 hover:border-purple-400 hover:bg-purple-50'}`}>
          <input
            ref={inputRef}
            type="file"
            multiple={isPhoto}
            accept={isPhoto ? 'image/*' : '*/*'}
            className="hidden"
            onChange={e => handleFiles(e.target.files)}
          />
          <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
          <p className="text-sm font-medium text-gray-600">
            {dragOver ? 'Lepas file di sini' : 'Klik atau drag file ke sini'}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {isPhoto ? 'JPG, PNG, WEBP' : 'Semua format'} · maks{' '}
            {selectedDefinition?.maxFileSizeMb ?? 10}MB per file
            {isPhoto ? ' · bisa multiple file' : ''}
          </p>
        </div>
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((item, idx) => (
            <div key={idx}
              className={`flex items-center gap-3 p-2.5 rounded-xl border ${
                item.error
                  ? 'border-red-200 bg-red-50'
                  : 'border-gray-200 bg-white'}`}>
              {item.preview ? (
                <img src={item.preview} alt=""
                  className="w-10 h-10 rounded-lg object-cover flex-shrink-0 border border-gray-200" />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center
                  justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{item.file.name}</p>
                <p className={`text-xs ${item.error ? 'text-red-600' : 'text-gray-400'}`}>
                  {item.error ?? formatFileSize(item.file.size)}
                </p>
              </div>
              <button
                onClick={() => removeFile(idx)}
                className="text-gray-400 hover:text-red-500 flex-shrink-0 p-1">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Caption */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">
          Keterangan (opsional)
        </label>
        <input
          type="text"
          value={caption}
          onChange={e => setCaption(e.target.value)}
          placeholder="Contoh: Foto galian sisi utara, kedalaman 80cm"
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm
            focus:outline-none focus:ring-2 focus:ring-purple-500 bg-gray-50"
        />
      </div>
    </div>
  );
};

// ============================================================================
// MAIN MODAL
// ============================================================================

/**
 * @param {object|null} preselectedScope
 *   Bila diisi: skip step 1, langsung ke step 2 (milestone).
 *   Format: { id, name, code, projectName, scopeType: 'FO'|'TOWER' }
 *
 *   Dari header button  → preselectedScope = null  (3 steps)
 *   Dari segment card   → preselectedScope = group  (2 steps, scope sudah terpilih)
 */
export default function UploadEvidenceModal({ onClose, onSuccess, preselectedScope = null }) {
  const initialStep  = preselectedScope ? 2 : 1;
  const initialScope = preselectedScope ?? null;
 
  const [step,               setStep]               = useState(initialStep);
  const [scopeType,          setScopeType]          = useState(
    preselectedScope?.scopeType === 'TOWER' ? 'TOWER' : 'FO'
  );
  const [selectedScope,      setSelectedScope]      = useState(initialScope);
  const [selectedMilestone,  setSelectedMilestone]  = useState(null);
  const [selectedDefinition, setSelectedDefinition] = useState(null);
  const [files,              setFiles]              = useState([]);
  const [caption,            setCaption]            = useState('');
  const [saving,             setSaving]             = useState(false);
 
  const STEP_LABELS = ['Segment / Site', 'Milestone', 'Upload'];
 
  const minStep = preselectedScope ? 2 : 1;
  const maxStep = 3;
 
  const canNext = () => {
    if (step === 1) return selectedScope != null;
    if (step === 2) return selectedMilestone != null;
    if (step === 3) return files.length > 0 && files.every(f => !f.error);
    return false;
  };
 
  const handleSubmit = async () => {
    if (!selectedMilestone || files.length === 0) return;
    setSaving(true);
    let successCount = 0;
    let errorCount   = 0;
 
    for (const item of files) {
      try {
        await uploadEvidence(selectedMilestone.id, item.file, {
          evidenceDefinitionId: selectedDefinition?.id ?? null,
          caption: caption || null,
        });
        successCount++;
      } catch (err) {
        errorCount++;
        console.error('Upload failed:', item.file.name, err);
      }
    }
 
    setSaving(false);
 
    if (successCount > 0) {
      toast.success(`${successCount} file berhasil diupload. Menunggu review PM/PC.`);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} file gagal diupload`);
    }
 
    if (successCount > 0) {
      onSuccess?.();
      onClose();
    }
  };
 
  const stepLabel = preselectedScope
    ? `Langkah ${step - 1} dari 2`
    : `Langkah ${step} dari 3`;
 
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl border border-gray-200 w-full max-w-lg
        flex flex-col max-h-[90vh] overflow-hidden shadow-xl">
 
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-800 to-purple-600 px-5 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                <Upload className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">Upload Evidence</p>
                <p className="text-purple-200 text-xs">{stepLabel}</p>
              </div>
            </div>
            <button onClick={onClose}
              className="text-purple-200 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
 
        {/* Step indicator — step 1 langsung "done" bila preselectedScope ada */}
        <div className="border-b border-gray-100 flex-shrink-0">
          <StepIndicator current={step} labels={STEP_LABELS} />
        </div>
 
        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {step === 1 && (
            <Step1Scope
              scopeType={scopeType}
              setScopeType={setScopeType}
              selectedScope={selectedScope}
              setSelectedScope={setSelectedScope}
            />
          )}
          {step === 2 && (
            <Step2Milestone
              selectedScope={selectedScope}
              selectedMilestone={selectedMilestone}
              setSelectedMilestone={setSelectedMilestone}
            />
          )}
          {step === 3 && (
            <Step3Upload
              selectedScope={selectedScope}
              selectedMilestone={selectedMilestone}
              selectedDefinition={selectedDefinition}
              setSelectedDefinition={setSelectedDefinition}
              files={files}
              setFiles={setFiles}
              caption={caption}
              setCaption={setCaption}
            />
          )}
        </div>
 
        {/* Footer */}
        <div className="border-t border-gray-100 px-5 py-3 flex items-center
          justify-between flex-shrink-0 bg-gray-50">
          <div>
            {step > minStep && (
              <button
                onClick={() => setStep(s => s - 1)}
                className="px-4 py-2 text-sm border border-gray-200 rounded-xl
                  hover:bg-gray-100 transition-colors text-gray-600">
                ← Kembali
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose}
              className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">
              Batal
            </button>
            {step < maxStep ? (
              <button
                onClick={() => setStep(s => s + 1)}
                disabled={!canNext()}
                className="flex items-center gap-2 px-5 py-2 bg-purple-700 text-white
                  text-sm font-medium rounded-xl hover:bg-purple-800 transition-colors
                  disabled:opacity-40 disabled:cursor-not-allowed">
                Lanjut <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={saving || !canNext()}
                className="flex items-center gap-2 px-5 py-2 bg-purple-700 text-white
                  text-sm font-medium rounded-xl hover:bg-purple-800 transition-colors
                  disabled:opacity-40">
                <Upload className="w-4 h-4" />
                {saving
                  ? `Mengupload ${files.length} file...`
                  : `Upload ${files.length} File`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}